import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BedDouble, Coffee, Globe, Landmark, Loader2, MapPin, Phone, ShoppingBag, Star, UtensilsCrossed, Wine } from "lucide-react";
import { PortalShell } from "@/components/AppHeader";
import { AdSlot } from "@/components/AdSlot";
import { api, errMsg } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ICONS = { restaurants: UtensilsCrossed, bars: Wine, shopping: ShoppingBag, coffee: Coffee, attractions: Landmark, hotels: BedDouble };

const PlaceCard = ({ p, onRide }) => {
  const Icon = ICONS[p.category] || MapPin;
  return (
    <div data-testid={`guide-place-${p.id}`} className="lux-card flex flex-col overflow-hidden fade-up">
      {p.photo_url
        ? <img src={`${BACKEND}${p.photo_url}`} alt={p.name} className="h-40 w-full object-cover" />
        : <div className="flex h-40 w-full items-center justify-center bg-gradient-to-br from-[#1a1710] to-[#0e0e12]" data-testid={`guide-place-placeholder-${p.id}`}><Icon className="h-10 w-10 text-amber-400/70" /></div>}
      <div className="flex flex-1 flex-col gap-2 p-5">
        <div className="flex items-start justify-between gap-3">
          <h3 data-testid={`guide-place-name-${p.id}`} className="font-serif text-xl text-amber-50">{p.name}</h3>
          {p.featured && <span data-testid={`guide-place-featured-${p.id}`} className="shrink-0 rounded-full border border-amber-400/50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-amber-300">Featured</span>}
          {p.rating && <span className="flex shrink-0 items-center gap-1 text-sm text-amber-200"><Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />{p.rating} <span className="text-xs text-zinc-500">({p.rating_count})</span></span>}
        </div>
        <p className="flex items-start gap-1.5 text-xs text-zinc-400"><MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-400" />{p.address}{p.distance_miles != null && <span className="ml-1 text-zinc-500">· {p.distance_miles} mi</span>}</p>
        {p.description && <p className="text-sm text-zinc-300">{p.description}</p>}
        <div className="mt-auto flex flex-wrap items-center gap-3 pt-3 text-xs">
          {p.phone && <a href={`tel:${p.phone}`} data-testid={`guide-place-phone-${p.id}`} className="flex items-center gap-1 text-amber-300 hover:text-amber-200"><Phone className="h-3.5 w-3.5" />{p.phone}</a>}
          {p.website && <a href={p.website} target="_blank" rel="noopener noreferrer" data-testid={`guide-place-website-${p.id}`} className="flex items-center gap-1 text-amber-300 hover:text-amber-200"><Globe className="h-3.5 w-3.5" />Website</a>}
          <button data-testid={`guide-ride-here-${p.id}`} onClick={() => onRide(p)} disabled={p.lat == null} className="btn-gold ml-auto !px-4 !py-2 text-xs">Ride here</button>
        </div>
        {p.source === "google" && <p className="text-[10px] text-zinc-600">From Google{p.photo_attribution ? ` · Photo: ${p.photo_attribution}` : ""}</p>}
      </div>
    </div>
  );
};

export default function LocalGuide() {
  const nav = useNavigate();
  const [meta, setMeta] = useState(null);
  const [cat, setCat] = useState("restaurants");
  const [places, setPlaces] = useState(null);
  const [nearby, setNearby] = useState([]);
  const [error, setError] = useState("");
  useEffect(() => { api.get("/guide/categories").then(({ data }) => setMeta(data)).catch((e) => setError(errMsg(e))); }, []);
  useEffect(() => {
    setPlaces(null); setNearby([]);
    api.get("/guide/places", { params: { category: cat } }).then(({ data }) => setPlaces(data)).catch((e) => setError(errMsg(e)));
    if (meta?.google_enabled) api.get("/guide/nearby", { params: { category: cat } }).then(({ data }) => setNearby(data.places || [])).catch(() => {});
  }, [cat, meta?.google_enabled]);
  const rideHere = (p) => {
    sessionStorage.setItem("lux_dropoff", JSON.stringify({ address: `${p.name}, ${p.address}`, lat: p.lat, lng: p.lng }));
    nav("/customer");
  };
  return (
    <PortalShell>
      <div data-testid="local-guide-page" className="space-y-8">
        <div>
          <p className="lux-eyebrow">Dallas</p>
          <h1 className="mt-2 text-4xl font-semibold text-amber-50 sm:text-5xl">Local Guide</h1>
          <p className="mt-3 max-w-xl text-sm text-zinc-400">Our favourite places around town. Tap “Ride here” and we'll set it as your drop-off.</p>
        </div>
        <div className="-mx-1 flex gap-2 overflow-x-auto pb-1" data-testid="guide-categories">
          {(meta?.categories || []).map((c) => {
            const Icon = ICONS[c.key];
            return (
              <button key={c.key} data-testid={`guide-category-${c.key}`} onClick={() => setCat(c.key)}
                className={`flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors duration-300 ${cat === c.key ? "border-amber-400 bg-amber-400 text-black" : "border-white/10 text-zinc-300 hover:border-amber-500/40"}`}>
                <Icon className="h-4 w-4" />{c.label}
              </button>
            );
          })}
        </div>
        <AdSlot placement="local_guide" />
        {error && <p data-testid="guide-error" className="text-sm text-red-300">{error}</p>}
        {!places && !error && <Loader2 className="h-6 w-6 animate-spin text-amber-400" data-testid="guide-loading" />}
        {places?.length === 0 && <p data-testid="guide-empty" className="lux-card p-6 text-sm text-zinc-400">No places in this category yet.</p>}
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="guide-curated-list">
          {places?.map((p) => <PlaceCard key={p.id} p={p} onRide={rideHere} />)}
        </div>
        {nearby.length > 0 && (
          <div className="space-y-4" data-testid="guide-nearby-section">
            <h2 className="text-lg font-semibold text-amber-100">Top rated near you</h2>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{nearby.map((p) => <PlaceCard key={p.id} p={p} onRide={rideHere} />)}</div>
          </div>
        )}
      </div>
    </PortalShell>
  );
}
